import { adyenConfigSchema } from '@models/schema'
import { AdyenConfig } from '@models/venue/integrations/AdyenConfig'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  deleteAdyenConfig as deleteAdyenConfigAPI,
  fetchAdyenConfigById,
  fetchAdyenConfigs,
} from '@services/venue/integrations/AdyenConfigService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<AdyenConfig> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'adyenConfig',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getAdyenConfigsSuccess,
  setQuery: setAdyenConfigQuery,
  reset: resetAdyenConfigState,
  resetQueryAndIds: resetAdyenConfigQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: AdyenConfig }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectAdyenConfigs = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedAdyenConfig = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectAdyenConfigById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectAdyenConfigsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedAdyenConfig =
  (adyenConfig: AdyenConfig | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<AdyenConfig>(adyenConfig))
    )

export const getAdyenConfigs = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchAdyenConfigs(params),
    [adyenConfigSchema],
    slice.actions
  )

export const getAdyenConfigWithId = (id: number) =>
  SBAPI.FetchDispatched<AdyenConfig>(
    fetchAdyenConfigById(id),
    adyenConfigSchema,
    slice.actions
  )

export const deleteAdyenConfig = (adyenConfig: AdyenConfig) =>
  SBAPI.DestroyDispatched<AdyenConfig>(
    deleteAdyenConfigAPI(adyenConfig),
    adyenConfig,
    slice.actions
  )
